import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PRODUCTS } from '../mock/mock';
import { useCart } from '../context/CartContext';
import { ChevronLeft, Minus, Plus, Shield, Truck, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const SIZES = ['XS', 'S', 'M', 'L', 'XL'];

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [size, setSize] = useState('M');
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem } = useCart();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!product) {
    return (
      <main className="bg-cream min-h-[60vh] flex flex-col items-center justify-center px-5">
        <h1 className="mega text-brown text-5xl">Not Found</h1>
        <button onClick={() => navigate('/shop')} className="btn-primary px-6 py-3 font-anton uppercase tracking-widest text-sm mt-6">Back to Shop</button>
      </main>
    );
  }

  const related = PRODUCTS.filter((p) => p.id !== product.id).slice(0, 4);

  const handleAdd = () => {
    addItem(product, qty, size);
    toast(`${product.name} added to bag`);
  };

  return (
    <main className="bg-cream">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-8">
        <Link to="/shop" className="inline-flex items-center gap-1 font-anton uppercase tracking-widest text-xs text-brown/70 hover:text-brown">
          <ChevronLeft className="w-4 h-4" /> Back to shop
        </Link>
      </div>

      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-12 grid grid-cols-12 gap-6 md:gap-10">
        {/* Images */}
        <div className="col-span-12 md:col-span-7">
          <div className={`relative aspect-[4/5] overflow-hidden ${
            product.color === 'pink' ? 'bg-pink' : product.color === 'green' ? 'bg-green' : product.color === 'brown' ? 'bg-brown' : 'bg-cream-2'
          }`}>
            <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            {product.badge && (
              <span className="absolute top-4 left-4 font-anton uppercase tracking-widest text-[10px] bg-cream text-brown px-2 py-1">
                {product.badge}
              </span>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {product.images.map((src, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden border ${activeImg === i ? 'border-brown' : 'border-transparent'}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="col-span-12 md:col-span-5">
          <p className="font-anton uppercase tracking-[0.3em] text-pink-hot text-xs mb-3">{product.category}</p>
          <h1 className="font-display text-brown text-3xl md:text-5xl uppercase leading-tight">{product.name}</h1>
          <p className="font-display text-brown text-2xl mt-4">${product.price.toFixed(2)}</p>
          <p className="font-body text-brown/80 mt-5 leading-relaxed">{product.description}</p>

          {/* Size */}
          {product.category === 'apparel' && (
            <div className="mt-8">
              <p className="font-anton uppercase tracking-widest text-xs text-brown/70 mb-3">Size</p>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((s) => (
                  <button key={s} onClick={() => setSize(s)}
                    className={`w-12 h-12 font-anton uppercase text-xs border transition-colors rounded-full ${
                      size === s ? 'bg-brown text-cream border-brown' : 'bg-cream text-brown border-brown/30 hover:border-brown'
                    }`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {/* Qty */}
          <div className="mt-6">
            <p className="font-anton uppercase tracking-widest text-xs text-brown/70 mb-3">Quantity</p>
            <div className="inline-flex items-center border border-brown/30 rounded-full overflow-hidden">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-brown hover:text-pink-hot"><Minus className="w-4 h-4" /></button>
              <span className="px-6 font-body text-brown">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 text-brown hover:text-pink-hot"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col gap-3">
            <button onClick={handleAdd} className="btn-primary py-4 px-6 font-anton uppercase tracking-widest text-sm">
              Add to Bag — ${(product.price * qty).toFixed(2)}
            </button>
            <button className="btn-pink py-4 px-6 font-anton uppercase tracking-widest text-sm">Buy it now</button>
          </div>

          {/* Info blocks */}
          <div className="mt-10 grid grid-cols-1 gap-3">
            {[
              { icon: Truck, label: 'Free US shipping over $75' },
              { icon: RotateCcw, label: '30-day returns on unworn items' },
              { icon: Shield, label: 'Officially licensed merchandise' }
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 py-3 border-t border-brown/15">
                <b.icon className="w-4 h-4 text-brown/60" />
                <p className="font-body text-xs uppercase tracking-widest text-brown/80">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 pb-20 md:pb-28">
        <div className="flex items-end justify-between mb-8 border-t border-brown/15 pt-10">
          <h2 className="mega text-brown text-4xl md:text-6xl">You Might Also Like</h2>
          <Link to="/shop" className="font-anton uppercase tracking-widest text-xs text-brown hover:text-pink-hot">Shop All →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {related.map((p) => (
            <Link to={`/shop/${p.slug}`} key={p.id} className="product-card group block">
              <div className={`relative aspect-[4/5] overflow-hidden ${
                p.color === 'pink' ? 'bg-pink' : p.color === 'green' ? 'bg-green' : p.color === 'brown' ? 'bg-brown' : 'bg-cream-2'
              }`}>
                <img src={p.images[0]} alt={p.name} className="product-img w-full h-full object-cover" />
              </div>
              <div className="mt-3 flex items-start justify-between gap-2">
                <h3 className="font-body text-brown text-sm leading-snug">{p.name}</h3>
                <p className="font-display text-brown text-sm whitespace-nowrap">${p.price.toFixed(2)}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
